import { clamp } from "@/shared/math";

import { Log } from "./log";

export interface IndexRange {
  start: number;
  end: number;
}

/**
 * Lightweight index mapping display positions to log metadata.
 * Enables efficient navigation and lookup without loading full log content.
 * Note:
 *  1. Multiple logs can have identical timestamps.
 *  2. Sequence numbers are assumed to be stable identities.
 */
interface LogIndex {
  /** Display position → timestamp (nanoseconds). Used for time-based navigation and binary search. */
  timestamps: BigUint64Array;
  /**
   * Display position → sequence number (parallel to timestamps).
   * Sequence numbers are stable identifiers for logs, used as keys into the log cache.
   * Unlike display position, a log's sequence survives filter changes, enabling cache reuse.
   * The index into this array represents the display position (i.e., "the 0th log shown, the 1st log shown, etc.")
   * The value at each index is the log's sequence number, which is used as the key to look up the actual Log data in the cache.
   *
   * Float64 (not Uint32): merged multi-file topics share one row id space
   * (offset + per-topic id) that can exceed 2^32. Float64 holds integer ids
   * exactly to 2^53, matching the plain-number `Log.sequence` cache key.
   */
  sequences: Float64Array;
}

interface Opts {
  /**
   * Count of logs to fetch at once.
   *
   * Ideally, batchSize would be 1:1 with size of MCAP chunks. This would be the most efficient read strategy,
   * but we should not expect chunks to be consistently sized between or even within MCAP files.
   * Some MCAP chunks may contain 5 messages, others may contain 5000.
   * It depends on how they were written and the types of messages they contain.
   * Also, consider that data transfered between JS realms that is not a Transferable pays a structured clone cost,
   * so for a given average message data size, there's some upper bound on how many messages it makes sense
   * to attempt to copy between realms at once:
   *  > serialization blocks the sending realm, while deserialization blocks the receiving realm
   *  Ref: https://surma.dev/things/is-postmessage-slow/
   */
  batchSize: number;
  /** Max number of logs to cache */
  maxSize: number;
  /**
   * The time in milliseconds after which data is considered stale.
   * If set to Infinity, the data will never be considered stale.
   */
  staleTime: number;
}

export enum ScrollDirection {
  Forward = "forward", // scrolling forward in time
  Backward = "backward", // scrolling backward in time
}

export class LogCache {
  private batchSize: number;

  // Index mapping display positions to timestamps and sequence numbers
  private index: LogIndex = {
    timestamps: new BigUint64Array(0),
    sequences: new Float64Array(0),
  };

  // Cached log data, keyed by sequence. Persists across filter changes for reuse.
  private logCache: Map<number, Log> = new Map();

  // Per-position match state array, aligned with this.index.
  // 1 = match, 0 = non-match. null when no filter is applied or in hide mode.
  private matchStates: Uint8Array | null = null;

  private maxCacheSize: number;

  // Tracks when each sequence was added to cache (for staleness checks)
  // Separate from logCache to support efficient stale-while-revalidate:
  // clearing this map marks all cached logs as stale without discarding them.
  private sequenceToTimeAddedToCacheMap = new Map<
    number,
    DOMHighResTimeStamp
  >();
  private staleTime: number;

  constructor(opts?: Partial<Opts>) {
    this.batchSize = opts?.batchSize ?? 300;
    this.maxCacheSize = opts?.maxSize ?? 2_000;
    this.staleTime = opts?.staleTime ?? Number.POSITIVE_INFINITY;
  }

  public get logCount() {
    return this.index.timestamps.length;
  }

  public get isTimestampIndexLoaded() {
    return this.index.timestamps.length > 0;
  }

  public addLogsToCache(logs: Log[]) {
    const now = performance.now() + performance.timeOrigin;
    for (const log of logs) {
      this.logCache.set(log.sequence, log);
      this.sequenceToTimeAddedToCacheMap.set(log.sequence, now);
    }
  }

  public clearMatchStates(): void {
    this.matchStates = null;
  }

  public dispose() {
    this.logCache.clear();
    this.index = {
      timestamps: new BigUint64Array(0),
      sequences: new Float64Array(0),
    };
    this.matchStates = null;
    this.sequenceToTimeAddedToCacheMap.clear();
  }

  /**
   * Returns true if matchStates is null (no active dim-mode filter) or if the row at
   * displayIndex is marked as a match. Returns false for non-matching rows and for
   * out-of-bounds indices when matchStates is set.
   */
  public isMatching(displayIndex: number): boolean {
    if (!this.matchStates) {
      return true;
    }
    return this.matchStates[displayIndex] === 1;
  }

  public setMatchStates(states: Uint8Array): void {
    if (states.length !== this.index.sequences.length) {
      throw new Error(
        `matchStates length ${states.length} does not match index length ${this.index.sequences.length}`,
      );
    }
    this.matchStates = states;
  }

  /**
   * Maps a requested window of indices to a fixed-size bucket to ensure consistent data fetching.
   *
   * Each bucket has size #batchSize. Buckets are aligned to multiples of batchSize (e.g., 0-249, 250-499).
   * The method determines which bucket to return based on:
   * 1. The midpoint of the requested range
   * 2. The scroll direction
   * 3. The position relative to current bucket's midpoint
   *
   * When scrolling forward:
   * - Returns next bucket if midpoint >= current bucket's midpoint
   * - Otherwise returns current bucket
   *
   * When scrolling backward:
   * - Returns previous bucket if midpoint <= current bucket's midpoint
   * - Otherwise returns current bucket
   */
  public bucketize(
    range: IndexRange,
    scrollDirection = ScrollDirection.Forward,
    playbackRate = 1,
  ): IndexRange {
    const { start, end } = range;
    // Make batch size a function of the speed with which we need to render data.
    // The constants are ultimately arbitrary. But see docstring on `Opts.batchSize` for more details.
    const MIN_RATE = 1;
    const MAX_RATE = 2.5;
    const size = this.batchSize * clamp(playbackRate, MIN_RATE, MAX_RATE);

    const midpoint = Math.floor((start + end) / 2);
    const currentBucketNumber = Math.floor(midpoint / size);

    // If we're in the first half of current bucket and scrolling backward,
    // or in the second half and scrolling forward, move to adjacent bucket
    const bucketMidpoint = currentBucketNumber * size + size / 2;
    const shouldMoveToAdjacentBucket =
      scrollDirection === ScrollDirection.Forward
        ? midpoint >= bucketMidpoint
        : midpoint <= bucketMidpoint;

    const targetBucketNumber = shouldMoveToAdjacentBucket
      ? currentBucketNumber +
        (scrollDirection === ScrollDirection.Forward ? 1 : -1)
      : currentBucketNumber;

    const bucketStart = targetBucketNumber * size;
    const bucketEnd = bucketStart + size - 1;

    // TODO(gabe): This is working better moving forward than backward.
    const minIndex = Math.max(0, Math.min(start, bucketStart));
    const maxIndex = Math.min(
      this.index.timestamps.length - 1,
      Math.max(end, bucketEnd),
    );

    return { start: minIndex, end: maxIndex };
  }

  /**
   * Identifies index ranges that need to be fetched from the worker.
   *
   * @param range - The visible window of indices to check for missing data.
   * @param scrollDirection - Direction of scroll, used to determine which bucket to prefetch.
   * @param playbackRate - Playback speed multiplier, scales the batch size for prefetching.
   * @param maxGap - Maximum gap between ranges to coalesce. Set to 0 to disable coalescing.
   * @returns Object containing missing ranges and whether the input range has missing data.
   */
  public getIndicesForMissingData(
    range: IndexRange,
    scrollDirection = ScrollDirection.Forward,
    playbackRate = 1,
    maxGap = this.batchSize,
  ): {
    missing: IndexRange[];
    isInputRangeInMissing: boolean;
  } {
    const { start, end } = this.bucketize(range, scrollDirection, playbackRate);

    const missing: IndexRange[] = [];
    let rangeStart: number | null = null;
    let isInputRangeInMissing = false;

    for (let i = start; i <= end; i++) {
      // if the log isn't yet cached or is stale (stale-while-revalidate)
      const sequence = this.index.sequences[i];
      const isMissing =
        !this.logCache.has(sequence) || this.isCacheStaleAtSequence(sequence);

      if (isMissing) {
        // Check if this index falls within the original unbucketized range
        // This would mean the list virtualizer doesn't have the data it needs to render
        if (i >= range.start && i <= range.end) {
          isInputRangeInMissing = true;
        }

        if (rangeStart === null) {
          rangeStart = i;
        }
      } else if (rangeStart !== null) {
        missing.push({ start: rangeStart, end: i - 1 });
        rangeStart = null;
      }
    }

    if (rangeStart !== null && rangeStart <= end) {
      missing.push({ start: rangeStart, end: end });
    }

    return {
      missing: this.coalesceRanges(missing, maxGap),
      isInputRangeInMissing,
    };
  }

  public indexToLog(index: number): Log | undefined {
    const sequence = this.index.sequences[index];
    return this.logCache.get(sequence);
  }

  /**
   * Display position → sequence, read straight off the timestamp index without
   * requiring the corresponding {@link Log} to be cached.
   *
   * Prefer this over `indexToLog(index)?.sequence` anywhere a display position
   * needs a stable identity while logs are still streaming. The timestamp index
   * lands in one shot (see {@link setTimestampIndex}), whereas the log cache
   * fills in progressively — so a sequence read through the cache flips from
   * undefined to its real value partway through a load, and any React key
   * derived from it remounts the row when it does.
   *
   * Returns undefined for a position the current index does not cover,
   * including every position before the first {@link setTimestampIndex} call.
   */
  public indexToSequence(index: number): number | undefined {
    if (index < 0 || index >= this.index.sequences.length) {
      return undefined;
    }
    return this.index.sequences[index];
  }

  public indexToTimestamp(index: number): bigint {
    return this.index.timestamps[index];
  }

  public markCacheStale() {
    this.sequenceToTimeAddedToCacheMap.clear();
  }

  public setTimestampIndex(
    timestamps: BigUint64Array,
    sequences: Float64Array,
  ) {
    if (timestamps.length !== sequences.length) {
      const msg = [
        "Something went wrong while loading this topic's data.",
        "Try removing active filters (if any) and/or reloading the panel.",
        "Please contact Roboto support if this persists.",
      ].join(" ");

      const cause = [
        "Timestamp index used by LogPanel is invalid. Timestamps and sequences must be the same length.",
        `Timestamps length: ${timestamps.length}, sequences length: ${sequences.length}`,
      ].join(" ");

      throw new Error(msg, { cause });
    }

    this.index = { timestamps, sequences };
  }

  public timestampToIndex(time: bigint): number {
    let low = 0;
    let high = this.index.timestamps.length - 1;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const logTime = this.index.timestamps[mid];
      if (logTime === time) {
        return mid;
      } else if (logTime < time) {
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    // Use the index of the closest log before the given time otherwise
    return Math.max(low - 1, 0);
  }

  /**
   * Evicts cached logs outside a padded window centered on the current view.
   *
   * The keep window extends equally in both directions from currentWindow,
   * up to maxCacheSize total entries. Logs are identified for eviction by
   * building a Set of sequences that fall within the keep window, then
   * removing any cached log whose sequence is not in that Set.
   */
  public trimCache(currentDisplayWindow: IndexRange) {
    if (this.logCache.size < this.maxCacheSize) {
      return;
    }

    const { start, end } = currentDisplayWindow;
    const windowSize = end - start + 1;

    // Calculate keep range with padding (or no padding if window exceeds max)
    const padding =
      windowSize >= this.maxCacheSize
        ? 0
        : Math.floor((this.maxCacheSize - windowSize) / 2);

    const keepStart = Math.max(0, start - padding);
    const keepEnd = Math.min(this.index.timestamps.length - 1, end + padding);

    // Build set of sequences to keep
    const keepSequences = new Set<number>();
    for (let i = keepStart; i <= keepEnd; i++) {
      keepSequences.add(this.index.sequences[i]);
    }

    // Evict logs not in the keep set
    for (const sequence of this.logCache.keys()) {
      if (!keepSequences.has(sequence)) {
        this.clearCacheAtSequence(sequence);
      }
    }
  }

  private clearCacheAtSequence(sequence: number) {
    this.logCache.delete(sequence);
    this.sequenceToTimeAddedToCacheMap.delete(sequence);
  }

  /**
   * Coalesces adjacent missing ranges into fewer, larger ranges to reduce request count.
   * Merges two ranges if the gap between them is within maxGap and the combined span fits within batchSize.
   * Accepts over-fetching of already-cached data as a trade-off for fewer round trips for data.
   *
   * @param ranges - Array of missing index ranges to potentially coalesce
   * @param maxGap - Maximum gap size between ranges to allow merging. Set to 0 to disable coalescing.
   */
  private coalesceRanges(ranges: IndexRange[], maxGap: number): IndexRange[] {
    if (ranges.length <= 1 || maxGap <= 0) {
      return ranges;
    }

    const result: IndexRange[] = [{ ...ranges[0] }];

    for (let i = 1; i < ranges.length; i++) {
      const current = ranges[i];
      const last = result[result.length - 1];
      const gap = current.start - last.end - 1;
      const mergedSize = current.end - last.start + 1;

      if (gap <= maxGap && mergedSize <= this.batchSize) {
        // Merge: extend the last range to include current
        last.end = current.end;
      } else {
        result.push({ ...current });
      }
    }

    return result;
  }

  private isCacheStaleAtSequence(sequence: number): boolean {
    const cachedAt = this.sequenceToTimeAddedToCacheMap.get(sequence) ?? -1;
    if (cachedAt === -1) {
      return true;
    }
    const now = performance.now() + performance.timeOrigin;
    return now - cachedAt > this.staleTime;
  }
}
